import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { HistoryFacade } from '../history-entries';
import { WorkspaceFacade } from '../workspace';
import { ChatMessage, DbDiagramLayout, DbSchema } from './db-design.models';
import { DbDesignActions } from './db-design.actions';
import {
  selectDbChatMessages,
  selectDbError,
  selectDbLayout,
  selectDbLoading,
  selectDbProjectDefinition,
  selectDbRepoContext,
  selectDbSchema,
} from './db-design.selectors';

@Injectable({ providedIn: 'root' })
export class DbDesignFacade {
  private store = inject(Store);
  private workspace = inject(WorkspaceFacade);
  private history = inject(HistoryFacade);

  readonly schema = toSignal(this.store.select(selectDbSchema), { initialValue: null });
  readonly layout = toSignal(this.store.select(selectDbLayout), { initialValue: null });
  readonly chatMessages = toSignal(this.store.select(selectDbChatMessages), { initialValue: [] as ChatMessage[] });
  readonly projectDefinition = toSignal(this.store.select(selectDbProjectDefinition), { initialValue: null });
  readonly repoContext = toSignal(this.store.select(selectDbRepoContext), { initialValue: null });
  readonly loading = toSignal(this.store.select(selectDbLoading), { initialValue: false });
  readonly error = toSignal(this.store.select(selectDbError), { initialValue: null });
  readonly activeProjectId = this.workspace.activeProjectId;

  load(): void {
    this.store.dispatch(DbDesignActions.load());
  }
  saveSchema(schema: DbSchema | null): void {
    this.store.dispatch(DbDesignActions.saveSchema({ schema }));
  }
  setSchemaDraft(schema: DbSchema | null): void {
    this.store.dispatch(DbDesignActions.setSchemaDraft({ schema }));
  }
  saveLayout(layout: DbDiagramLayout | null): void {
    this.store.dispatch(DbDesignActions.saveLayout({ layout }));
  }
  setLayoutDraft(layout: DbDiagramLayout | null): void {
    this.store.dispatch(DbDesignActions.setLayoutDraft({ layout }));
  }
  setChatMessages(messages: ChatMessage[]): void {
    this.store.dispatch(DbDesignActions.setChatMessages({ messages }));
  }
  async logChatEvent(text: string, kind: 'user' | 'assistant' | 'info' | 'update' | 'error' = 'info'): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    await this.history.add(projectId, 'db-design.chat', text, kind as any);
  }
}
